'use client';

import { type FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScreenContent, ScreenFixedTop } from '../../screen';
import { ErrorMessage } from '../../components/ErrorMessage';
import { WinnerListSkeleton } from '../../components/WinnerListSkeleton';
import { WinnerRow } from '../../components/WinnerRow';
import { useApiClient } from '../../contexts/ApiContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { formatDayMonth } from '../../i18n/formatDate';
import { queryKeys } from '../../lib/queryClient';
import { TypeEnum1 } from '../../services/generated/Api';
import translations from './DrawsHistoryScreen.translations';

interface DrawsHistoryScreenProps {
  params?: { drawType?: TypeEnum1 };
}

export const DrawsHistoryScreen: FC<DrawsHistoryScreenProps> = ({ params }) => {
  const { t, lang } = useLocalTranslation(translations);
  const apiClient = useApiClient();
  const navigation = useNavigation();

  const drawType = params?.drawType;

  const handleUserClick = (userId: string) => {
    navigation.navigate('profile', { userId });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('timeJustNow');
    if (diffMins < 60) return t('timeMinutesAgo', { count: diffMins });
    if (diffHours < 24) return t('timeHoursAgo', { count: diffHours });
    if (diffDays < 7) return t('timeDaysAgo', { count: diffDays });

    return formatDayMonth(date, lang);
  };

  const {
    data: winners = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.drawsHistory(drawType),
    queryFn: () =>
      apiClient.api.drawsControllerGetWinnersHistory({
        limit: 20,
        type: drawType,
      }),
  });

  if (error && winners.length === 0) {
    return (
      <>
        <ScreenFixedTop title={t('title')} />
        <ScreenContent className="flex-1">
          <ErrorMessage
            error={t('loadError')}
            onRetry={() => refetch()}
          />
        </ScreenContent>
      </>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenContent className="flex-1">
        {isLoading && winners.length === 0 ? (
          <WinnerListSkeleton thirdLineHasIcon={false} />
        ) : winners.length === 0 ? (
          <div className="px-5 py-10 text-center text-base text-white/60">{t('empty')}</div>
        ) : (
          <div className="flex flex-col gap-2 p-4">
            {winners.map((winner) => (
              <WinnerRow
                key={winner.id}
                userId={winner.winner.id}
                firstName={winner.winner.firstName}
                lastName={winner.winner.lastName}
                level={winner.winner.level}
                prize={{ name: winner.prizeItemName, imageUrl: winner.prizeItemImage, tinted: false }}
                meta={formatDate(winner.completedAt)}
                thirdLineHasIcon={false}
                onClick={() => handleUserClick(winner.winner.id)}
              />
            ))}
          </div>
        )}
      </ScreenContent>
    </>
  );
};
